// @ts-nocheck
/* Starter Sync - <EditNotesForm> from MyListsDetailsProductList */

import translate from "@insite/client-framework/Translate";
import Button, { ButtonPresentationProps } from "@insite/mobius/Button";
import GridContainer, { GridContainerProps } from "@insite/mobius/GridContainer";
import GridItem, { GridItemProps } from "@insite/mobius/GridItem";
import Modal, { ModalPresentationProps } from "@insite/mobius/Modal";
import TextArea, { TextAreaProps } from "@insite/mobius/TextArea";
import constants from "@store/constants";
import FormActions from "@store/Library/Components/Toolkit/FormActions";
import { formGridProps } from "@store/Themes/common/props/form";
import useExpandProps, { ExpandStyles } from "@toolkit/Framework/Common/Hooks/useExpandProps";
import React, { FC, useEffect, useState } from "react";

export interface EditNotesFormModalProps extends ExpandStyles<EditNotesFormModalStyles> {
    notes: string;
    testSelector?: string;
    isDisabled?: boolean;
    modalIsOpen: boolean;
    modalTitle?: string;
    onCancel: (event: React.MouseEvent<HTMLElement, MouseEvent>) => void;
    onClose?: (event: React.MouseEvent<HTMLElement, MouseEvent>) => void;
    onSubmit: (notes: string) => void;
}

export interface EditNotesFormModalStyles {
    modal?: ModalPresentationProps;
    container?: GridContainerProps;
    gridItem?: GridItemProps;
    textArea?: TextAreaProps;
    deleteButton?: ButtonPresentationProps;
    cancelButton?: ButtonPresentationProps;
    submitButton?: ButtonPresentationProps;
}

export const editNotesFormModalStyles: EditNotesFormModalStyles = {
    modal: { sizeVariant: "medium" },
    container: formGridProps.gridContainer,
    gridItem: { width: 12 },
    deleteButton: { variant: "tertiary" },
    cancelButton: { variant: "secondary" },
};

const EditNotesFormModal: FC<EditNotesFormModalProps> = ({
    notes,
    testSelector,
    isDisabled,
    modalIsOpen,
    modalTitle,
    onCancel,
    onSubmit,
    onClose,
    extendedStyles,
    overridedStyles,
}) => {
    const styles = useExpandProps(editNotesFormModalStyles, extendedStyles, overridedStyles);
    const [notesDraft, setNotesDraft] = useState(notes);

    useEffect(() => {
        if (modalIsOpen) {
            setNotesDraft(notes);
        }
    }, [modalIsOpen]);

    const notesChangeHandler = (event: React.ChangeEvent<HTMLTextAreaElement>) => {
        const value = event.target.value;
        setNotesDraft(
            value.length > constants.notesMaxCharacters ? value.slice(0, constants.notesMaxCharacters) : value,
        );
    };

    const formDeleteHandler = (event: React.MouseEvent<HTMLElement, MouseEvent>) => {
        event.preventDefault();
        setNotesDraft("");
        onSubmit("");
    };

    const formSubmitHandler = (event: React.MouseEvent<HTMLElement, MouseEvent>) => {
        event.preventDefault();
        onSubmit(notesDraft);
    };

    const formCancelHandler = (event: React.MouseEvent<HTMLElement, MouseEvent>) => {
        event.preventDefault();
        setNotesDraft(notes || "");
        onCancel(event);
    };

    const formCloseHandler = (event: React.MouseEvent<HTMLElement, MouseEvent>) => {
        event.preventDefault();
        if (onClose) {
            setNotesDraft(notes || "");
            onClose(event);
        } else {
            setNotesDraft(notes || "");
            onCancel(event);
        }
    };

    return (
        <Modal
            {...styles.modal}
            headline={modalTitle || notes ? translate("Edit Notes") : translate("Add Notes")}
            isOpen={modalIsOpen}
            handleClose={formCloseHandler}
        >
            <GridContainer {...styles.container}>
                <GridItem {...styles.gridItem}>
                    <TextArea
                        {...styles.textArea}
                        value={notesDraft}
                        onChange={notesChangeHandler}
                        hint={`${constants.notesMaxCharacters - (notesDraft?.length || 0)} ${translate(
                            "characters left",
                        )}`}
                        disabled={isDisabled}
                        placeholder={translate("Enter Line Notes")}
                    />
                </GridItem>
            </GridContainer>
            <FormActions>
                <Button
                    {...styles.submitButton}
                    onClick={formSubmitHandler}
                    data-test-selector={`${testSelector}_submitNoteButton`}
                >
                    {translate("Save")}
                </Button>
                {notes && (
                    <Button
                        {...styles.deleteButton}
                        onClick={formDeleteHandler}
                        data-test-selector={`${testSelector}_deleteNoteButton`}
                    >
                        {translate("Delete Notes")}
                    </Button>
                )}
                <Button
                    {...styles.cancelButton}
                    onClick={formCancelHandler}
                    data-test-selector={`${testSelector}_cancelNoteButton`}
                >
                    {translate("Cancel")}
                </Button>
            </FormActions>
        </Modal>
    );
};

export default EditNotesFormModal;
